<template>
  <div class="marker-tab">
    <div class="tab-header">
      <el-button v-if="canEdit" type="primary" size="small" @click="openAddDialog">
        마카 추가
      </el-button>
    </div>

    <el-table :data="markers" stripe size="small" border>
      <el-table-column prop="marker_name" label="마카이름" min-width="120" />
      <el-table-column prop="material_name" label="원단명" min-width="120" />
      <el-table-column prop="marker_type_display" label="구분" width="80" align="center" />
      <el-table-column label="원단폭" width="100" align="right">
        <template #default="{ row }">
          {{ row.fabric_width }} {{ row.fabric_width_unit_display }}
        </template>
      </el-table-column>
      <el-table-column label="마카길이(m)" width="110" align="right">
        <template #default="{ row }">{{ row.marker_length }}</template>
      </el-table-column>
      <el-table-column label="효율(%)" width="90" align="right">
        <template #default="{ row }">{{ row.efficiency }}%</template>
      </el-table-column>
      <el-table-column prop="size_ratio" label="사이즈비율" min-width="140" />
      <el-table-column label="CAD" width="60" align="center">
        <template #default="{ row }">
          <el-link v-if="row.cad_file" :href="row.cad_file" target="_blank" type="primary">파일</el-link>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column v-if="canEdit" label="작업" width="100" align="center">
        <template #default="{ row }">
          <el-button size="small" type="primary" link @click="openEditDialog(row)">수정</el-button>
          <el-button size="small" type="danger" link @click="deleteMarker(row)">삭제</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-empty v-if="markers.length === 0" description="등록된 마카가 없습니다" />

    <!-- 추가/수정 다이얼로그 -->
    <el-dialog
      v-model="showDialog"
      :title="isEdit ? '마카 수정' : '마카 추가'"
      width="550px"
      destroy-on-close
    >
      <el-form :model="form" label-width="100px">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="마카이름" required>
              <el-input v-model="form.marker_name" placeholder="마카이름" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="구분" required>
              <el-select v-model="form.marker_type" style="width: 100%">
                <el-option label="샘플" value="SAMPLE" />
                <el-option label="벌크" value="BULK" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="원단명" required>
          <el-input v-model="form.material_name" placeholder="원단명" />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="원단폭">
              <el-input-number v-model="form.fabric_width" :precision="2" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="폭 단위">
              <el-select v-model="form.fabric_width_unit" style="width: 100%">
                <el-option label="인치" value="INCH" />
                <el-option label="센티미터" value="CM" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="마카길이(m)">
              <el-input-number v-model="form.marker_length" :precision="2" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="효율(%)">
              <el-input-number v-model="form.efficiency" :precision="2" :min="0" :max="100" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="사이즈비율">
          <el-input v-model="form.size_ratio" placeholder="예: S:M:L:XL = 1:2:2:1" />
        </el-form-item>
        <el-form-item label="CAD 파일">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :limit="1"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
          >
            <el-button size="small" type="primary">파일 선택</el-button>
            <template #tip>
              <div v-if="isEdit && editData?.cad_file_name" class="upload-tip">
                현재 파일: {{ editData.cad_file_name }}
              </div>
            </template>
          </el-upload>
        </el-form-item>
        <el-form-item label="메모">
          <el-input v-model="form.memo" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showDialog = false">취소</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">
          {{ isEdit ? '수정' : '추가' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  designId: { type: [String, Number], required: true },
  markers: { type: Array, default: () => [] },
  canEdit: { type: Boolean, default: false }
})

const emit = defineEmits(['updated'])

const showDialog = ref(false)
const isEdit = ref(false)
const editData = ref(null)
const submitting = ref(false)
const selectedFile = ref(null)

const form = reactive({
  marker_name: '',
  material_name: '',
  marker_type: 'SAMPLE',
  fabric_width: 0,
  fabric_width_unit: 'INCH',
  marker_length: 0,
  efficiency: 0,
  size_ratio: '',
  memo: ''
})

const resetForm = () => {
  form.marker_name = ''
  form.material_name = ''
  form.marker_type = 'SAMPLE'
  form.fabric_width = 0
  form.fabric_width_unit = 'INCH'
  form.marker_length = 0
  form.efficiency = 0
  form.size_ratio = ''
  form.memo = ''
  selectedFile.value = null
}

const openAddDialog = () => {
  isEdit.value = false
  editData.value = null
  resetForm()
  showDialog.value = true
}

const openEditDialog = (row) => {
  isEdit.value = true
  editData.value = row
  form.marker_name = row.marker_name
  form.material_name = row.material_name
  form.marker_type = row.marker_type
  form.fabric_width = Number(row.fabric_width)
  form.fabric_width_unit = row.fabric_width_unit
  form.marker_length = Number(row.marker_length)
  form.efficiency = Number(row.efficiency)
  form.size_ratio = row.size_ratio || ''
  form.memo = row.memo || ''
  selectedFile.value = null
  showDialog.value = true
}

const handleFileChange = (file) => {
  selectedFile.value = file.raw
}

const handleFileRemove = () => {
  selectedFile.value = null
}

const submitForm = async () => {
  if (!form.marker_name || !form.material_name) {
    ElMessage.warning('마카이름과 원단명은 필수입니다.')
    return
  }

  submitting.value = true
  try {
    const formData = new FormData()
    formData.append('marker_name', form.marker_name)
    formData.append('material_name', form.material_name)
    formData.append('marker_type', form.marker_type)
    formData.append('fabric_width', form.fabric_width)
    formData.append('fabric_width_unit', form.fabric_width_unit)
    formData.append('marker_length', form.marker_length)
    formData.append('efficiency', form.efficiency)
    formData.append('size_ratio', form.size_ratio)
    formData.append('memo', form.memo)
    if (selectedFile.value) {
      formData.append('cad_file', selectedFile.value)
      formData.append('cad_file_name', selectedFile.value.name)
    }

    if (isEdit.value) {
      await axiosInstance.put(
        `designs/${props.designId}/markers/${editData.value.id}/`,
        formData
      )
      ElMessage.success('마카가 수정되었습니다.')
    } else {
      await axiosInstance.post(
        `designs/${props.designId}/markers/`,
        formData
      )
      ElMessage.success('마카가 추가되었습니다.')
    }

    showDialog.value = false
    emit('updated')
  } catch (error) {
    ElMessage.error('저장 중 오류가 발생했습니다.')
  } finally {
    submitting.value = false
  }
}

const deleteMarker = async (row) => {
  try {
    await ElMessageBox.confirm(`"${row.marker_name}" 마카를 삭제하시겠습니까?`, '확인')
    await axiosInstance.delete(`designs/${props.designId}/markers/${row.id}/`)
    ElMessage.success('마카가 삭제되었습니다.')
    emit('updated')
  } catch {
    // cancelled
  }
}
</script>

<style scoped>
.marker-tab {
  padding: 20px;
}
.tab-header {
  margin-bottom: 15px;
}
.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}
</style>
